// @flow
import { get } from 'lodash';
import { downloadOrShare, isReportDownloadByEmail, isReportEmailOnlyPeriod, reportPeriodFilters, reportError } from '@helpers/helpers';
import { Api } from '@helpers';
import { router } from '@router';
import type
{
  Action,
  Dispatch,
  ContextState,
  ReportPeriodFilter,
} from '../../../types';
import { REPORT_TOAST_ID, closeToastById, reportPreparingToast, reportSentByEmailToast, reportErrorToast } from './toasts';

// ------------------------------------
// Constants
// ------------------------------------
const REPORTS_APPOINTMENTS_LOADING = 'reportsAppointments.REPORTS_APPOINTMENTS_LOADING';
const REPORTS_APPOINTMENTS_LOADED = 'reportsAppointments.REPORTS_APPOINTMENTS_LOADED';
const REPORTS_APPOINTMENTS_DOWNLOADING = 'reportsAppointments.REPORTS_APPOINTMENTS_DOWNLOADING';
const REPORTS_APPOINTMENTS_DOWNLOADED = 'reportsAppointments.REPORTS_APPOINTMENTS_DOWNLOADED';
const REPORTS_APPOINTMENTS_LOADING_FAILED = 'reportsAppointments.REPORTS_APPOINTMENTS_LOADING_FAILED';

export const getReportsAppointments = (startDate: string, endDate: string, doctorId: number | null, periodFilter?: ReportPeriodFilter) =>
  (dispatch: Dispatch) => {
    const queryFilters = {
      'filter[doctorId]': doctorId,
      'filter[startDate]': startDate,
      'filter[endDate]': endDate,
      ...reportPeriodFilters(periodFilter),
    };

    dispatch({
      type: REPORTS_APPOINTMENTS_LOADING,
      filters: {
        startDate, endDate, doctorId,
      },
    });
    Api.get(router.getApiRoute('getReportsAppointments', {}), {
      query: queryFilters,
      success: (response) => {
        dispatch({
          type: REPORTS_APPOINTMENTS_LOADED,
          appointments: get(response, ['appointmentsSummary']),
        });
      },
      error: (error) => {
        dispatch({ type: REPORTS_APPOINTMENTS_LOADING_FAILED, error: { message: error.message } });
        dispatch(reportError(error));
      },
    });
  };

export const getReportsAppointmentsDownload = (startDate: string, endDate: string, doctorId: number | null, periodFilter?: ReportPeriodFilter) =>
  (dispatch: Dispatch, getState: Function) => {
    dispatch(reportPreparingToast);
    dispatch({ type: REPORTS_APPOINTMENTS_DOWNLOADING });
    const queryFilters = {
      'filter[doctorId]': doctorId,
      'filter[startDate]': startDate || null,
      'filter[endDate]': endDate || null,
      ...reportPeriodFilters(periodFilter),
    };

    const userEmail = get(getState(), ['context', 'user', 'email']);
    const isDownload = !isReportDownloadByEmail(getState) && !isReportEmailOnlyPeriod(get(periodFilter, 'period'));
    const route = router.getApiRoute('getReportsAppointmentsDownload', {});


    if (isDownload) {
      downloadOrShare({
        url: route.path,
        state: getState(),
        mime: 'application/vnd.ms-excel',
        fileName: 'report_appointments_{t}.xlsx',
        dispatch,
        params: queryFilters,
      }).then(({ shared, sentByEmail }) => {
        if (sentByEmail) {
          dispatch(reportSentByEmailToast(userEmail));
        } else if (!shared) {
          dispatch(closeToastById(REPORT_TOAST_ID));
        }
        dispatch({
          type: REPORTS_APPOINTMENTS_DOWNLOADED,
        });
      }).catch((error) => {
        dispatch(reportErrorToast(error.userMessage));
        dispatch({ type: REPORTS_APPOINTMENTS_LOADING_FAILED, error: { message: error.message } });
      });
    } else {
      Api.get(route, {
        query: queryFilters,
        success: () => {
          dispatch(reportSentByEmailToast(userEmail));
          dispatch({
            type: REPORTS_APPOINTMENTS_DOWNLOADED,
          });
        },
        error: (error) => {
          dispatch(reportErrorToast(get(error, ['raw', 0, 'errorMessages', 0])));
          dispatch({ type: REPORTS_APPOINTMENTS_LOADING_FAILED, error: { message: error.message } });
          dispatch(reportError(error));
        },
      });
    }
  };

// ------------------------------------
// Reducer
// ------------------------------------
export default function reportsAppointmentsReducer (state: ContextState, action: Action): ContextState {
  switch (action.type) {
    case REPORTS_APPOINTMENTS_LOADING:
      return state
        .setIn(['reportsAppointments', 'isLoading'], true)
        .setIn(['reportsAppointments', 'reportRequested'], false)
        .setIn(['reportsAppointments', 'filters'], get(action, 'filters', {}))
        .setIn(['reportsAppointments', 'errors'], null);

    case REPORTS_APPOINTMENTS_LOADED:
      return state
        .setIn(['reportsAppointments', 'isLoading'], false)
        .setIn(['reportsAppointments', 'data'], get(action, 'appointments', {}))
        .setIn(['reportsAppointments', 'errors'], null);

    // reportRequested stays true after DOWNLOADED: the download stays disabled
    // until the filters change (which refetches the report) to avoid queueing
    // the same heavy report on the backend multiple times
    case REPORTS_APPOINTMENTS_DOWNLOADING:
      return state
        .setIn(['reportsAppointments', 'reportRequested'], true);

    case REPORTS_APPOINTMENTS_DOWNLOADED:
      return state
        .setIn(['reportsAppointments', 'isLoading'], false)
        .setIn(['reportsAppointments', 'errors'], null);

    case REPORTS_APPOINTMENTS_LOADING_FAILED:
      return state
        .setIn(['reportsAppointments', 'isLoading'], false)
        .setIn(['reportsAppointments', 'reportRequested'], false)
        .setIn(['reportsAppointments', 'errors'], [action.error.message]);

    default:
      return state;
  }
}
